import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "../../config/httpConfig";
import { handleUploadFile } from "../../helpers/helpers";
import { rejectJVDeal } from "./JointVenturesDealsSlice";

export { rejectJVDeal };

export const getDealSectionMeta = createAsyncThunk(
  "dealApproval/getDealSectionMeta",
  async (dealId, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `/api/admin/getDealSectionMeta?dealId=${encodeURIComponent(dealId)}`,
      );
      return response?.response || response || {};
    } catch (err) {
      return rejectWithValue(
        err.response?.data ||
          err.message ||
          "Failed to fetch deal section metadata",
      );
    }
  },
);

export const getDealSections = createAsyncThunk(
  "dealApproval/getDealSections",
  async (dealId, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `/api/admin/getDealSections?dealId=${encodeURIComponent(dealId)}`,
      );
      return response?.response || response || [];
    } catch (err) {
      return rejectWithValue(
        err.response?.data || err.message || "Failed to fetch deal sections",
      );
    }
  },
);

export const addDealSectionMeta = createAsyncThunk(
  "dealApproval/addDealSectionMeta",
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.post(
        "/api/admin/addDealSectionMeta",
        payload,
      );
      if (payload?.dealId) {
        await dispatch(getDealSectionMeta(payload.dealId));
      }
      return response?.response || response || {};
    } catch (err) {
      return rejectWithValue(
        err.response?.data || err.message || "Failed to add section metadata",
      );
    }
  },
);

export const deleteDealSectionMeta = createAsyncThunk(
  "dealApproval/deleteDealSectionMeta",
  async ({ sectionId, dealId }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.delete(
        `/api/admin/deleteDealSectionMeta/${encodeURIComponent(sectionId)}?dealId=${encodeURIComponent(dealId)}`,
      );
      if (dealId) {
        await dispatch(getDealSectionMeta(dealId));
      }
      return response?.response || response || "Section deleted successfully";
    } catch (err) {
      return rejectWithValue(
        err.response?.data ||
          err.message ||
          "Failed to delete section metadata",
      );
    }
  },
);

export const getAllDealsList = createAsyncThunk(
  "dealApproval/getAllDealsList",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get("/api/admin/allDealsList");
      return response?.response || response || [];
    } catch (err) {
      return rejectWithValue(
        err.response?.data || err.message || "Failed to fetch deals list",
      );
    }
  },
);

export const getDealInterestInvestors = createAsyncThunk(
  "dealApproval/getDealInterestInvestors",
  async ({ dealId, interestStatus }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `/api/admin/getDealInterestInvestors?dealId=${dealId}&interestStatus=${interestStatus}`,
      );
      return response?.response;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

export const sendPitchEmail = createAsyncThunk(
  "dealApproval/sendPitchEmail",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.post(`/api/admin/sendPitchEmail`, payload);
      return response;
    } catch (err) {
      return rejectWithValue(err || "An error occurred");
    }
  },
);

export const addDealSection = createAsyncThunk(
  "dealApproval/addDealSection",
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.post("/api/admin/addDealSection", payload);
      if (payload?.dealId) {
        await dispatch(getDealSections(payload.dealId));
      }
      return response?.response || response || {};
    } catch (err) {
      return rejectWithValue(
        err.response?.data || err.message || "Failed to add deal section",
      );
    }
  },
);

export const updateDealSection = createAsyncThunk(
  "dealApproval/updateDealSection",
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      const { dealId, ...requestPayload } = payload || {};
      const response = await axios.put(
        "/api/admin/updateDealSection",
        requestPayload,
      );
      if (payload?.dealId) {
        await dispatch(getDealSections(payload.dealId));
      }
      return response?.response || response || {};
    } catch (err) {
      return rejectWithValue(
        err.response?.data || err.message || "Failed to update deal section",
      );
    }
  },
);

export const deleteDealSection = createAsyncThunk(
  "dealApproval/deleteDealSection",
  async ({ dealId, contentId }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.delete(
        `/api/admin/deleteDealSection/${encodeURIComponent(contentId)}`,
      );
      if (dealId) {
        await dispatch(getDealSections(dealId));
      }
      return (
        response?.message ||
        response?.response ||
        "Deal section deleted successfully"
      );
    } catch (err) {
      return rejectWithValue(
        err.response?.data || err.message || "Failed to delete deal section",
      );
    }
  },
);

export const getDealSectionOptions = createAsyncThunk(
  "dealApproval/getDealSectionOptions",
  async (optionType, { rejectWithValue }) => {
    try {
      const hasOptionType =
        typeof optionType === "string" && optionType.trim().length > 0;
      const endpoint = hasOptionType
        ? `/api/admin/getDealSectionOptions?optionType=${encodeURIComponent(optionType)}`
        : "/api/admin/getDealSectionOptions";
      const response = await axios.get(endpoint);
      return response?.response || response || {};
    } catch (err) {
      return rejectWithValue(
        err.response?.data ||
          err.message ||
          "Failed to fetch deal section options",
      );
    }
  },
);

export const approveJVDeal = createAsyncThunk(
  "dealApproval/approveJVDeal",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.post("/api/admin/approveJVDeal", payload);
      return response?.response || response || {};
    } catch (err) {
      return rejectWithValue(
        err.response?.data || err.message || "Failed to approve deal",
      );
    }
  },
);

export const uploadDealApprovalFile = createAsyncThunk(
  "dealApproval/uploadDealApprovalFile",
  async ({ file, folderPath }, { rejectWithValue }) => {
    try {
      if (!file) {
        return rejectWithValue("No file selected");
      }

      const { fileUrl, fileName } = await handleUploadFile(file, folderPath);

      return {
        fileName,
        fileType: file.type,
        folderPath,
        fileUrl,
      };
    } catch (err) {
      return rejectWithValue(
        err?.response?.data || err?.message || "Failed to upload file",
      );
    }
  },
);

const dealApprovalSlice = createSlice({
  name: "dealApproval",
  initialState: {
    sections: [],
    blockTypes: [],
    options: [],
    dealSectionOptions: {
      allOptions: [],
      titleOptions: [],
      bulletColors: [],
      bulletPrefixes: [],
      metricPrefixes: [],
      metricSuffixes: [],
      statusOptions: [],
    },
    dealSections: [],
    allDealsList: [],
    loading: false,
    dealSectionOptionsLoading: false,
    dealSectionsLoading: false,
    allDealsListLoading: false,
    addDealSectionLoading: false,
    addDealSectionMetaLoading: false,
    deleteDealSectionMetaLoading: false,
    updateDealSectionLoading: false,
    deleteDealSectionLoading: false,
    uploadFileLoading: false,
    error: null,
    dealSectionOptionsError: null,
    dealSectionsError: null,
    allDealsListError: null,
    addDealSectionError: null,
    addDealSectionMetaError: null,
    deleteDealSectionMetaError: null,
    updateDealSectionError: null,
    deleteDealSectionError: null,
    uploadFileError: null,
    uploadedFiles: [],
    approveJVDealLoading: false,
    approveJVDealError: null,
    rejectJVDealLoading: false,
    rejectJVDealError: null,
    dealInterestInvestors: [],
    pitchEmailLoading: false,
    pitchEmailError: null
  },
  reducers: {
    updateErrorStatus(state) {
      state.pitchEmailError = "";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getDealSectionMeta.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getDealSectionMeta.fulfilled, (state, action) => {
        state.loading = false;
        state.sections = Array.isArray(action.payload?.sections)
          ? action.payload.sections
          : [];
        state.blockTypes = Array.isArray(action.payload?.blockTypes)
          ? action.payload.blockTypes
          : [];
        state.options = Array.isArray(action.payload?.allOptions)
          ? action.payload.allOptions
          : Array.isArray(action.payload?.options)
            ? action.payload.options
            : [];
      })
      .addCase(getDealSectionMeta.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch deal section metadata";
      })
      .addCase(getDealSectionOptions.pending, (state) => {
        state.dealSectionOptionsLoading = true;
        state.dealSectionOptionsError = null;
      })
      .addCase(getDealSectionOptions.fulfilled, (state, action) => {
        state.dealSectionOptionsLoading = false;
        const payload = action.payload || {};
        const previousOptions = state.dealSectionOptions;

        const nextAllOptions = Array.isArray(payload?.allOptions)
          ? payload.allOptions
          : [];
        const hasAllOptions = nextAllOptions.length > 0;
        state.dealSectionOptions = {
          allOptions: hasAllOptions
            ? nextAllOptions
            : previousOptions.allOptions,
          titleOptions: Array.isArray(payload?.titleOptions)
            ? payload.titleOptions
            : previousOptions.titleOptions,
          bulletColors: Array.isArray(payload?.bulletColors)
            ? payload.bulletColors
            : previousOptions.bulletColors,
          bulletPrefixes: Array.isArray(payload?.bulletPrefixes)
            ? payload.bulletPrefixes
            : previousOptions.bulletPrefixes,
          metricPrefixes: Array.isArray(payload?.metricPrefixes)
            ? payload.metricPrefixes
            : previousOptions.metricPrefixes,
          metricSuffixes: Array.isArray(payload?.metricSuffixes)
            ? payload.metricSuffixes
            : previousOptions.metricSuffixes,
          statusOptions: Array.isArray(payload?.statusOptions)
            ? payload.statusOptions
            : previousOptions.statusOptions,
        };
      })
      .addCase(getDealSectionOptions.rejected, (state, action) => {
        state.dealSectionOptionsLoading = false;
        state.dealSectionOptionsError =
          action.payload || "Failed to fetch deal section options";
      })
      .addCase(getDealSections.pending, (state) => {
        state.dealSectionsLoading = true;
        state.dealSectionsError = null;
      })
      .addCase(getDealSections.fulfilled, (state, action) => {
        state.dealSectionsLoading = false;
        state.dealSections = Array.isArray(action.payload)
          ? action.payload
          : [];
      })
      .addCase(getDealSections.rejected, (state, action) => {
        state.dealSectionsLoading = false;
        state.dealSectionsError =
          action.payload || "Failed to fetch deal sections";
      })
      .addCase(getAllDealsList.pending, (state) => {
        state.allDealsListLoading = true;
        state.allDealsListError = null;
      })
      .addCase(getAllDealsList.fulfilled, (state, action) => {
        state.allDealsListLoading = false;
        state.allDealsList = Array.isArray(action.payload)
          ? action.payload
          : [];
      })
      .addCase(getAllDealsList.rejected, (state, action) => {
        state.allDealsListLoading = false;
        state.allDealsListError =
          action.payload || "Failed to fetch deals list";
      })
      .addCase(addDealSection.pending, (state) => {
        state.addDealSectionLoading = true;
        state.addDealSectionError = null;
      })
      .addCase(addDealSection.fulfilled, (state) => {
        state.addDealSectionLoading = false;
      })
      .addCase(addDealSection.rejected, (state, action) => {
        state.addDealSectionLoading = false;
        state.addDealSectionError =
          action.payload || "Failed to add deal section";
      })
      .addCase(addDealSectionMeta.pending, (state) => {
        state.addDealSectionMetaLoading = true;
        state.addDealSectionMetaError = null;
      })
      .addCase(addDealSectionMeta.fulfilled, (state) => {
        state.addDealSectionMetaLoading = false;
      })
      .addCase(addDealSectionMeta.rejected, (state, action) => {
        state.addDealSectionMetaLoading = false;
        state.addDealSectionMetaError =
          action.payload || "Failed to add section metadata";
      })
      .addCase(deleteDealSectionMeta.pending, (state) => {
        state.deleteDealSectionMetaLoading = true;
        state.deleteDealSectionMetaError = null;
      })
      .addCase(deleteDealSectionMeta.fulfilled, (state) => {
        state.deleteDealSectionMetaLoading = false;
      })
      .addCase(deleteDealSectionMeta.rejected, (state, action) => {
        state.deleteDealSectionMetaLoading = false;
        state.deleteDealSectionMetaError =
          action.payload || "Failed to delete section metadata";
      })
      .addCase(updateDealSection.pending, (state) => {
        state.updateDealSectionLoading = true;
        state.updateDealSectionError = null;
      })
      .addCase(updateDealSection.fulfilled, (state) => {
        state.updateDealSectionLoading = false;
      })
      .addCase(updateDealSection.rejected, (state, action) => {
        state.updateDealSectionLoading = false;
        state.updateDealSectionError =
          action.payload || "Failed to update deal section";
      })
      .addCase(deleteDealSection.pending, (state) => {
        state.deleteDealSectionLoading = true;
        state.deleteDealSectionError = null;
      })
      .addCase(deleteDealSection.fulfilled, (state) => {
        state.deleteDealSectionLoading = false;
      })
      .addCase(deleteDealSection.rejected, (state, action) => {
        state.deleteDealSectionLoading = false;
        state.deleteDealSectionError =
          action.payload || "Failed to delete deal section";
      })
      .addCase(approveJVDeal.pending, (state) => {
        state.approveJVDealLoading = true;
        state.approveJVDealError = null;
      })
      .addCase(approveJVDeal.fulfilled, (state) => {
        state.approveJVDealLoading = false;
      })
      .addCase(approveJVDeal.rejected, (state, action) => {
        state.approveJVDealLoading = false;
        state.approveJVDealError = action.payload || "Failed to approve deal";
      })
      .addCase(rejectJVDeal.pending, (state) => {
        state.rejectJVDealLoading = true;
        state.rejectJVDealError = null;
      })
      .addCase(rejectJVDeal.fulfilled, (state) => {
        state.rejectJVDealLoading = false;
      })
      .addCase(rejectJVDeal.rejected, (state, action) => {
        state.rejectJVDealLoading = false;
        state.rejectJVDealError = action.payload || "Failed to reject deal";
      })
      .addCase(uploadDealApprovalFile.pending, (state) => {
        state.uploadFileLoading = true;
        state.uploadFileError = null;
      })
      .addCase(uploadDealApprovalFile.fulfilled, (state, action) => {
        state.uploadFileLoading = false;
        if (action.payload) {
          state.uploadedFiles = [...state.uploadedFiles, action.payload];
        }
      })
      .addCase(uploadDealApprovalFile.rejected, (state, action) => {
        state.uploadFileLoading = false;
        state.uploadFileError = action.payload || "Failed to upload file";
      })
      .addCase(getDealInterestInvestors.fulfilled, (state, action) => {
        state.dealInterestInvestors = action.payload;
      })
      .addCase(sendPitchEmail.pending, (state) => {
        state.pitchEmailLoading = true;
        state.pitchEmailError = null;
      })
      .addCase(sendPitchEmail.fulfilled, (state) => {
        state.pitchEmailLoading = false;
        state.pitchEmailError = null;
      })
      .addCase(sendPitchEmail.rejected, (state, action) => {
        state.pitchEmailLoading = false;
        state.pitchEmailError = action.payload;
      })
  },
});

export const { updateErrorStatus } = dealApprovalSlice.actions;

export default dealApprovalSlice.reducer;
